import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import { Logo, PAGE_CONTAINER } from "../../components/site";
import { Zoekprestaties } from "@/components/zoekprestaties";
import { Indexatie } from "@/components/indexatie";
import { Zoekwoorden } from "@/components/zoekwoorden";

export const Route = createFileRoute("/_authenticated/admin")({
  head: () => ({
    meta: [
      { title: "Beheer aanvragen | offerto" },
      { name: "description", content: "Beheer offerteaanvragen en zoekprestaties van offerto." },
      { property: "og:title", content: "Beheer aanvragen | offerto" },
      { property: "og:description", content: "Beheer offerteaanvragen en zoekprestaties." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AdminPage,
});

type Lead = Tables<"leads">;

const STATUS = ["nieuw", "in_behandeling", "offerte_verstuurd", "gepland", "afgerond", "geen_match"] as const;
const STATUS_LABEL: Record<string, string> = {
  nieuw: "Nieuw",
  in_behandeling: "In behandeling",
  offerte_verstuurd: "Offerte verstuurd",
  gepland: "Gepland",
  afgerond: "Afgerond",
  geen_match: "Geen match",
};

const PARTNER_STATUS_LABEL: Record<string, string> = {
  onbekend: "Nog niet bekend",
  ontvangen: "Ontvangen door partner",
  geaccepteerd: "Aangenomen door vakman",
  afgewezen: "Afgewezen door partner",
};

function partnerStatusVan(lead: Lead) {
  if (lead.partner_verzendstatus === "mislukt") {
    return { label: "Doorsturen mislukt", className: "bg-destructive/10 text-destructive" };
  }
  if (lead.partner_status === "geaccepteerd") {
    return { label: PARTNER_STATUS_LABEL["geaccepteerd"], className: "bg-primary/10 text-primary" };
  }
  if (lead.partner_status === "afgewezen") {
    return { label: PARTNER_STATUS_LABEL["afgewezen"], className: "bg-destructive/10 text-destructive" };
  }
  if (lead.partner_verzendstatus === "ontvangen") {
    return { label: "API ontvangen", className: "bg-primary/10 text-primary" };
  }
  return { label: "Nog niet doorgestuurd", className: "bg-secondary text-muted-foreground" };
}

function fotosVan(lead: Lead): { name: string; url: string }[] {
  return Array.isArray(lead.fotos) ? (lead.fotos as { name: string; url: string }[]) : [];
}

function puntenVan(lead: Lead): string[] {
  return Array.isArray(lead.ai_aandachtspunten) ? (lead.ai_aandachtspunten as string[]) : [];
}

type Offerteregel = {
  label: string;
  eenheid: string;
  aantal: number;
  regel_laag: number;
  regel_hoog: number;
  toelichting: string;
};

function offerteregelsVan(lead: Lead): Offerteregel[] {
  return Array.isArray(lead.offerteregels) ? (lead.offerteregels as unknown as Offerteregel[]) : [];
}

function euro(bedrag: number): string {
  return new Intl.NumberFormat("nl-NL", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(bedrag);
}


function AdminPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<string>("alle");
  const [openId, setOpenId] = useState<string | null>(null);
  const [tab, setTab] = useState<"aanvragen" | "zoek" | "indexatie" | "zoekwoorden">("aanvragen");

  const { data: isAdmin, isLoading: rolLaden } = useQuery({
    queryKey: ["is-admin"],
    queryFn: async () => {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) return false;
      const { data } = await supabase.rpc("has_role", {
        _user_id: userData.user.id,
        _role: "admin",
      });
      return Boolean(data);
    },
  });

  const { data: leads = [], isLoading } = useQuery({
    queryKey: ["leads"],
    enabled: isAdmin === true,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("leads")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  async function updateLead(id: string, velden: Partial<Lead>) {
    const { error } = await supabase.from("leads").update(velden).eq("id", id);
    if (!error) await queryClient.invalidateQueries({ queryKey: ["leads"] });
  }

  async function verwijderLead(id: string) {
    if (!window.confirm("Weet je zeker dat je deze aanvraag wilt verwijderen?")) return;
    const { error } = await supabase.from("leads").delete().eq("id", id);
    if (!error) {
      setOpenId(null);
      await queryClient.invalidateQueries({ queryKey: ["leads"] });
    }
  }

  async function uitloggen() {
    await supabase.auth.signOut();
    void navigate({ to: "/auth" });
  }

  if (rolLaden) {
    return <div className="p-10 text-center text-muted-foreground">Laden…</div>;
  }

  if (!isAdmin) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4 text-center">
        <h1 className="text-2xl font-bold">Geen toegang</h1>
        <p className="mt-2 max-w-md text-muted-foreground">
          Je bent ingelogd, maar je account heeft geen beheerrechten. Vraag een beheerder om je
          account toe te voegen.
        </p>
        <button
          type="button"
          onClick={() => void uitloggen()}
          className="mt-6 rounded-md border border-primary px-6 py-3 font-bold text-primary"
        >
          Uitloggen
        </button>
      </div>
    );
  }

  const gefilterd = filter === "alle" ? leads : leads.filter((l) => l.status === filter);
  const open = leads.find((l) => l.id === openId) ?? null;
  const tel = (s: string) => leads.filter((l) => l.status === s).length;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className={`${PAGE_CONTAINER} flex items-center justify-between py-4`}>
          <Logo />
          <div className="flex items-center gap-4">
            <span className="text-sm font-bold">Beheer</span>
            <button
              type="button"
              onClick={() => void uitloggen()}
              className="text-sm text-muted-foreground underline"
            >
              Uitloggen
            </button>
          </div>
        </div>
      </header>

      <main className={`${PAGE_CONTAINER} py-8`}>
        <Link to="/klantbeheer" className="mb-4 inline-block text-sm font-bold text-primary hover:underline">Klantaccounts beheren →</Link>
        <div className="mb-6 flex flex-wrap items-center gap-2 border-b border-border pb-3">
          <Link to="/dashboard" className="rounded-md border border-border px-4 py-2 text-sm font-bold text-primary hover:bg-secondary">
            Dashboard
          </Link>
          <Link to="/verzonden-aanvragen" className="rounded-md border border-border px-4 py-2 text-sm font-bold text-primary hover:bg-secondary">
            Verzonden aanvragen
          </Link>
          <Link to="/vakmannen-beheer" className="rounded-md border border-border px-4 py-2 text-sm font-bold text-primary hover:bg-secondary">
            Vakmannen
          </Link>
          <Link to="/nieuws-beheer" className="text-sm font-bold text-primary hover:underline">Nieuws</Link>
          <button
            type="button"
            onClick={() => setTab("aanvragen")}
            className={`rounded-lg px-4 py-2 text-sm font-bold ${
              tab === "aanvragen" ? "bg-primary text-primary-foreground" : "text-muted-foreground"
            }`}
          >
            Offerteaanvragen
          </button>
          <button
            type="button"
            onClick={() => setTab("zoek")}
            className={`rounded-lg px-4 py-2 text-sm font-bold ${
              tab === "zoek" ? "bg-primary text-primary-foreground" : "text-muted-foreground"
            }`}
          >
            Zoekprestaties
          </button>
          <button
            type="button"
            onClick={() => setTab("indexatie")}
            className={`rounded-lg px-4 py-2 text-sm font-bold ${
              tab === "indexatie" ? "bg-primary text-primary-foreground" : "text-muted-foreground"
            }`}
          >
            Indexatie
          </button>
          <button
            type="button"
            onClick={() => setTab("zoekwoorden")}
            className={`rounded-lg px-4 py-2 text-sm font-bold ${
              tab === "zoekwoorden" ? "bg-primary text-primary-foreground" : "text-muted-foreground"
            }`}
          >
            Zoekwoorden volgen
          </button>
        </div>

        {tab === "zoek" && <Zoekprestaties />}
        {tab === "indexatie" && <Indexatie />}
        {tab === "zoekwoorden" && <Zoekwoorden />}


        {tab === "aanvragen" && (
        <>
        <h1 className="text-2xl font-bold">Offerteaanvragen</h1>

        <div className="mt-5 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setFilter("alle")}
            className={`rounded-full border px-4 py-1.5 text-sm font-medium ${
              filter === "alle" ? "border-primary bg-primary text-primary-foreground" : "border-border"
            }`}
          >
            Alle ({leads.length})
          </button>
          {STATUS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setFilter(s)}
              className={`rounded-full border px-4 py-1.5 text-sm font-medium ${
                filter === s ? "border-primary bg-primary text-primary-foreground" : "border-border"
              }`}
            >
              {STATUS_LABEL[s]} ({tel(s)})
            </button>
          ))}
        </div>

        {isLoading && <p className="mt-10 text-center text-muted-foreground">Aanvragen laden…</p>}
        {!isLoading && gefilterd.length === 0 && (
          <p className="mt-10 text-center text-muted-foreground">
            Geen aanvragen gevonden{filter !== "alle" ? ` met status "${STATUS_LABEL[filter]}"` : ""}.
          </p>
        )}

        <div className="mt-6 space-y-3">
          {gefilterd.map((lead) => (
            <button
              key={lead.id}
              type="button"
              onClick={() => setOpenId(lead.id)}
              className="block w-full rounded-lg border border-border bg-card p-4 text-left transition-colors hover:border-primary"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-bold">
                  {lead.voornaam} {lead.achternaam}
                </span>
                <span className="flex flex-wrap items-center gap-2">
                  <span className={`rounded-full px-3 py-1 text-xs font-bold ${partnerStatusVan(lead).className}`}>
                    {partnerStatusVan(lead).label}
                  </span>
                  <span className="rounded-full bg-secondary px-3 py-1 text-xs font-bold">
                    {STATUS_LABEL[lead.status] ?? lead.status}
                  </span>
                </span>
              </div>
              <p className="mt-1 break-words text-sm text-muted-foreground">{lead.bron} · {lead.product || "—"}</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {lead.type_opdracht || "-"} · {lead.plaats} ·{" "}
                {new Date(lead.created_at).toLocaleString("nl-NL", {
                  day: "numeric",
                  month: "short",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </p>
            </button>
          ))}
        </div>
        </>
        )}
      </main>

      {open && (
        <div
          className="fixed inset-0 z-50 flex justify-end bg-foreground/40"
          onClick={() => setOpenId(null)}
        >
          <div
            className="h-full w-full max-w-xl overflow-y-auto bg-background p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold">
                  {open.voornaam} {open.achternaam}
                </h2>
                <p className="text-sm text-muted-foreground">
                  Aangemaakt op {new Date(open.created_at).toLocaleString("nl-NL")}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOpenId(null)}
                className="rounded-md border border-border px-3 py-1.5 text-sm font-bold"
                aria-label="Sluiten"
              >
                ×
              </button>
            </div>

            <div className="mt-6">
              <span className="text-sm font-bold">Opvolgstatus</span>
              <div className="mt-2 flex flex-wrap gap-2">
                {STATUS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => void updateLead(open.id, { status: s })}
                    className={`rounded-full border px-3 py-1.5 text-sm font-medium ${
                      open.status === s
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border"
                    }`}
                  >
                    {STATUS_LABEL[s]}
                  </button>
                ))}
              </div>
            </div>

            <section className="mt-6 rounded-lg border border-border p-4">
              <h3 className="text-sm font-bold">Interne offerteregels</h3>
              <p className="mt-1 text-xs text-muted-foreground">
                Alleen voor intern gebruik en de offertesoftware. De klant ziet deze lijst niet.
              </p>
              {offerteregelsVan(open).length === 0 ? (
                <p className="mt-3 text-sm text-muted-foreground">Nog geen regels samengesteld.</p>
              ) : (
                <>
                  <ul className="mt-3 space-y-2 text-sm">
                    {offerteregelsVan(open).map((r, i) => (
                      <li key={`${r.label}-${i}`} className="rounded-md bg-secondary/50 p-3">
                        <div className="flex flex-wrap justify-between gap-2">
                          <span className="font-medium">
                            {r.aantal} x {r.label} ({r.eenheid})
                          </span>
                          <span className="font-medium">
                            {euro(r.regel_laag)} tot {euro(r.regel_hoog)}
                          </span>
                        </div>
                        {r.toelichting && (
                          <p className="mt-1 text-xs text-muted-foreground">{r.toelichting}</p>
                        )}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-3 flex justify-between gap-4 text-sm font-bold">
                    <span>Totaalindicatie</span>
                    <span>
                      {euro(Number(open.offerte_totaal_laag ?? 0))} tot {euro(Number(open.offerte_totaal_hoog ?? 0))}
                    </span>
                  </div>
                </>
              )}
            </section>

            <section className="mt-6 rounded-lg border border-border p-4">

              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <h3 className="text-sm font-bold">Partner-API</h3>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Technische ontvangst staat los van acceptatie door een vakman.
                  </p>
                </div>
                <span className={`rounded-full px-3 py-1 text-xs font-bold ${partnerStatusVan(open).className}`}>
                  {partnerStatusVan(open).label}
                </span>
              </div>
              <dl className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">API-ontvangst</dt>
                  <dd className="text-right font-medium">
                    {open.partner_verzendstatus === "ontvangen" ? "Gelukt" : open.partner_verzendstatus === "mislukt" ? "Mislukt" : "Niet verstuurd"}
                    {open.partner_http_status ? ` (HTTP ${open.partner_http_status})` : ""}
                  </dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">Partnernummer</dt>
                  <dd className="break-all text-right font-medium">{open.partner_aanvraag_id || "-"}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">Acceptatiestatus</dt>
                  <dd className="text-right font-medium">
                    {PARTNER_STATUS_LABEL[open.partner_status] ?? open.partner_status}
                  </dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">Verzonden</dt>
                  <dd className="text-right">
                    {open.partner_verzonden_at ? new Date(open.partner_verzonden_at).toLocaleString("nl-NL") : "-"}
                  </dd>
                </div>
              </dl>
              {open.partner_antwoord && (
                <details className="mt-4 rounded-md bg-secondary/50 p-3 text-sm">
                  <summary className="cursor-pointer font-medium">Volledig partnerantwoord</summary>
                  <pre className="mt-2 overflow-x-auto whitespace-pre-wrap break-words text-xs text-muted-foreground">
                    {open.partner_antwoord}
                  </pre>
                </details>
              )}
              {open.partner_status === "onbekend" && open.partner_verzendstatus === "ontvangen" && (
                <p className="mt-4 rounded-md border border-border bg-secondary/50 p-3 text-xs text-muted-foreground">
                  De partner heeft de aanvraag technisch ontvangen. Of een vakman deze aanneemt, kan worden opgehaald zodra het status-adres van de partner bekend is.
                </p>
              )}
            </section>

            <section className="mt-6 rounded-lg border border-border p-4">
              <h3 className="text-sm font-bold">Contactgegevens</h3>
              <dl className="mt-2 space-y-1 text-sm">
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">Telefoon</dt>
                  <dd>
                    <a className="font-medium underline" href={`tel:${open.telefoon}`}>
                      {open.telefoon}
                    </a>
                  </dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">E-mail</dt>
                  <dd>
                    <a className="font-medium underline" href={`mailto:${open.email}`}>
                      {open.email}
                    </a>
                  </dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">Adres</dt>
                  <dd className="text-right">
                    {open.straat} {open.huisnummer}, {open.postcode} {open.plaats}
                  </dd>
                </div>
              </dl>
            </section>

            <section className="mt-4 rounded-lg border border-border p-4">
              <h3 className="text-sm font-bold">Aanvraag</h3>
              <dl className="mt-2 space-y-1 text-sm">
                <div className="flex justify-between gap-4"><dt className="text-muted-foreground">Bron</dt><dd className="break-words text-right">{open.bron}</dd></div>
                <div className="flex justify-between gap-4"><dt className="text-muted-foreground">Product</dt><dd className="min-w-0 break-words text-right">{open.product || "—"}</dd></div>
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">Type opdracht</dt>
                  <dd>{open.type_opdracht || "-"}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">Soort aanvraag</dt>
                  <dd>{open.soort_aanvraag || "-"}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">Uitvoerdatum</dt>
                  <dd>{open.uitvoerdatum || "-"}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">Intentie</dt>
                  <dd className="text-right">{open.intentie || "-"}</dd>
                </div>
              </dl>
              {open.omschrijving && (
                <p className="mt-3 whitespace-pre-wrap rounded-md bg-secondary/50 p-3 text-sm">
                  {open.omschrijving}
                </p>
              )}
            </section>

            {(open.ai_samenvatting || puntenVan(open).length > 0) && (
              <section className="mt-4 rounded-lg border border-border p-4">
                <h3 className="text-sm font-bold">AI-samenvatting</h3>
                {open.ai_samenvatting && (
                  <p className="mt-2 text-sm">{open.ai_samenvatting}</p>
                )}
                {open.ai_categorie && (
                  <p className="mt-2 text-sm">
                    <span className="font-bold">Categorie:</span> {open.ai_categorie}
                  </p>
                )}
                {puntenVan(open).length > 0 && (
                  <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                    {puntenVan(open).map((p) => (
                      <li key={p}>{p}</li>
                    ))}
                  </ul>
                )}
              </section>
            )}

            {fotosVan(open).length > 0 && (
              <section className="mt-4 rounded-lg border border-border p-4">
                <h3 className="text-sm font-bold">Foto's ({fotosVan(open).length})</h3>
                <div className="mt-3 grid grid-cols-3 gap-3">
                  {fotosVan(open).map((f, i) => (
                    <a key={i} href={f.url} target="_blank" rel="noreferrer">
                      <img
                        src={f.url}
                        alt={f.name}
                        loading="lazy"
                        className="aspect-square w-full rounded-md object-cover"
                      />
                    </a>
                  ))}
                </div>
              </section>
            )}

            <section className="mt-4">
              <label className="block">
                <span className="text-sm font-bold">Interne notitie</span>
                <textarea
                  rows={3}
                  defaultValue={open.notitie ?? ""}
                  placeholder="Bijv. welke elektricien is gekoppeld, afspraken, etc."
                  className="mt-2 w-full rounded-md border border-input bg-background p-3 text-sm outline-none focus:border-primary"
                  onBlur={(e) => {
                    if (e.target.value !== (open.notitie ?? "")) {
                      void updateLead(open.id, { notitie: e.target.value });
                    }
                  }}
                />
              </label>
            </section>

            <button
              type="button"
              onClick={() => void verwijderLead(open.id)}
              className="mt-6 text-sm font-medium text-destructive underline"
            >
              Aanvraag verwijderen
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
